import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { cloneDeep } from "lodash";
import { useNavigate } from "react-router-dom";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { useRoleController } from "../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddClaims } from "../../../../../queries/billings/claims/use-add-claims";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import UpsertClaim from "./UpsertClaim";
import {
  ADD_NEW_CLAIM_VALIDATION_SCHEMA,
  CLAIM_FORM_TYPE,
  INITIALVALUES,
} from "./claims-constants";

interface CreateClaimDrawerProps {
  disClosureHost: DisclosureHost;
  title?: string;
}

const CreateClaimDrawer = ({
  disClosureHost,
  title,
}: CreateClaimDrawerProps) => {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  // const [_, setSearchParams] = useSearchParams();
  // const { mutateAsync: updateClaims } = useUpdateClaims();
  const { mutateAsync: addClaims } = useAddClaims();
  const formik = useFormik<CLAIM_FORM_TYPE>({
    enableReinitialize: true,
    initialValues: disClosureHost.data
      ? {
          patient: {
            value: disClosureHost.data.patientId,
            label: disClosureHost.data.patientName,
            birthDate: disClosureHost.data.patientDob,
          },
          encounter: {
            value: disClosureHost.data.uuid,
            label: disClosureHost.data.type ?? "",
          },
          provider: {
            value: disClosureHost.data.providerId,
            label: disClosureHost.data.providerName,
          },
          encounterType: disClosureHost.data.type ?? undefined,
          // Prefill DOS and POS from the selected RFB record so they are not
          // left blank on the first Create Claim screen. The RFB row carries
          // the visit start time and mode; POS is derived from mode the same
          // way the encounter dropdown handler does in UpsertClaim.
          dateOfService:
            disClosureHost.data.startTime ?? disClosureHost.data.encounterDate,
          placeOfService:
            disClosureHost.data.mode === "VIRTUAL"
              ? "TELEHEALTH_INSIDE_PATIENT_HOME"
              : disClosureHost.data.mode === "IN_PERSON"
                ? "OFFICE"
                : undefined,
          reasonForVisit: disClosureHost.data.chiefComplaint,
        }
      : INITIALVALUES,
    validationSchema: ADD_NEW_CLAIM_VALIDATION_SCHEMA,
    onSubmit,
  });

  function onSubmit(values: any) {
    const getObj = (obj: { value: string; label: string }) =>
      obj ? { [obj.value]: obj.label } : undefined;

    const copiedValues: any = cloneDeep(values);
    const selectedPatient = getObj(values.patient);
    const selectedEncounter = getObj(values.encounter);
    const selectedProvider = getObj(values.provider);
    const selectedReferringProvider = getObj(values.referringProvider);
    const selectedBillingProvider = getObj(values.billingProvider);

    const transFormData: any = {
      ...copiedValues,
      patient: selectedPatient,
      payerInsuance: undefined,
      encounter: selectedEncounter,
      provider: selectedProvider,
      referringProvider: selectedReferringProvider,
      // The form seeds Place of Service as "" when nothing is picked; the
      // backend expects an absent value as null, not an empty string.
      placeOfService: copiedValues?.placeOfService || null,
      // Optional Referring Provider NPI — mapped onto the backend claim key the
      // same way the Update Claim form sends it.
      referringProviderNpi: copiedValues?.referringProviderNPINumber || null,
      referringProviderNPINumber: undefined,
      billingProvider: selectedBillingProvider,
      // Billing Provider = Practice Group (Billing Entity). Send the practice
      // name (tenant identifier); the backend resolves the practice NPI,
      // contact and billing address onto the claim.
      practiceName: getXtenantId(),
      partialSave: true,
    };

    const payload = {
      claim: transFormData,
      xTENANTID: getXtenantId() ?? undefined,
    };

    showToastAndReset(
      addClaims(payload as any)
      // false ? updateClaims(payload as any) : addClaims(payload as any)
    );
  }

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });

        navigate(
          PROVIDER_PATHS.BILLING +
            `claims/edit-claim?patientId=${formik.values?.patient?.value as any}&claimId=${res?.data?.data}&claimStatus=NEW`
        );
        // setSearchParams({
        //   patientId: formik.values.patient?.value as any,
        //   claimId: res?.data?.data,
        //   claimStatus: "NEW",
        // });
        formik.resetForm();
        disClosureHost.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  function closeAndReset() {
    formik.resetForm();
    disClosureHost.close();
  }

  const { hasUpdate: hasUpdateClaims } = useRoleController("Generate Claims");

  const handleSave = async () => {
    try {
      // Validate form first
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        // Mark only errored fields as touched so inline red errors render.
        // Do NOT call submitForm here — it rebuilds `touched` from `values`,
        // which drops keys absent from initialValues (e.g. serviceLocation,
        // billingProvider) and hides the errors again.
        await formik.setTouched(
          Object.keys(errors).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          ),
          false
        );
        return;
      }
      await formik.submitForm();
    } catch (error) {
      console.error("Error saving claims:", error);
    }
  };

  return (
    <SideSheetDrawer
      open={disClosureHost.opened}
      w="44vw"
      title={title}
      onClose={closeAndReset}
      footerActions={
        <>
          <Button variant="outlined" onClick={closeAndReset}>
            Cancel
          </Button>
          {hasUpdateClaims && (
            <Button onClick={handleSave} variant="contained">
              Save and Next
            </Button>
          )}
        </>
      }
    >
      <UpsertClaim
        formik={formik}
        isPatientLocked={Boolean(disClosureHost.data)}
      />
    </SideSheetDrawer>
  );
};

export default CreateClaimDrawer;
